/**
 * Sessione a cookie httpOnly (decisione 43).
 * ---------------------------------------------------------------------------
 * I token JWT NON passano mai da questo codice: il backend li scrive in
 * cookie `httpOnly` + `SameSite=Strict` e il browser li allega da solo.
 * Una XSS nella SPA non può leggerli — è il motivo della scelta, presa
 * esplicitamente dal committente dopo che la versione precedente di questo
 * modulo (access in memoria + refresh in localStorage) l'aveva annotata
 * come alternativa più forte che «cambia il contratto lato backend».
 *
 * Cosa RESTA da fare al JavaScript:
 *   · mandare `credentials` (il client lo fa già: `same-origin`);
 *   · allegare `X-CSRFToken` alle scritture — un cookie viaggia da solo,
 *     quindi il CSRF torna un problema, e il token sta nel cookie
 *     `csrftoken` NON httpOnly (è fatto per essere letto);
 *   · su un 401, tentare UNA volta `POST /auth/rinnovo/` e ripetere.
 *
 * Endpoint (schema `docs/openapi-v2.yaml`):
 *   POST /api/v2/auth/accesso/  {email, password} → {utente} + cookie
 *   POST /api/v2/auth/rinnovo/  (vuoto, refresh nel cookie) → nuovi cookie
 *   POST /api/v2/auth/uscita/   → cancella i cookie
 *   GET  /api/v2/auth/io/       → UtenteCorrente (401 se sessione assente)
 */

import type { UtenteCorrente } from './types';

/** Deduplica i rinnovi concorrenti: N richieste in 401 insieme → 1 rinnovo. */
let refreshInFlight: Promise<boolean> | null = null;

/** Base dell'API. Duplicata da `client.ts` per non creare un ciclo di import. */
function apiBase(): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api/v2';
  return base.endsWith('/') ? base.slice(0, -1) : base;
}

/**
 * Il token CSRF dal cookie `csrftoken`. Stringa vuota se non c'è ancora:
 * il backend lo semina alla prima risposta di login.
 */
export function csrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : '';
}

/**
 * Rinnova la sessione. Il refresh token è nel cookie: non c'è nulla da
 * leggere o scrivere qui, solo da chiamare l'endpoint. `true` = la sessione
 * è di nuovo valida e la richiesta fallita va ripetuta.
 */
export async function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${apiBase()}/auth/rinnovo/`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      return response.ok;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Utente corrente, o `null` se non c'è una sessione. All'avvio del back
 * office è QUESTA la verifica: i cookie non sono leggibili, l'unico modo di
 * sapere se la sessione esiste è chiederlo al backend. Un 401 tenta il
 * rinnovo una volta (l'access dura 15 minuti, il refresh 8 ore: al rientro
 * dalla pausa pranzo è il caso normale, non l'eccezione).
 */
export async function fetchUtenteCorrente(
  signal?: AbortSignal,
): Promise<UtenteCorrente | null> {
  const chiama = (): Promise<Response> =>
    fetch(`${apiBase()}/auth/io/`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      ...(signal ? { signal } : {}),
    });

  try {
    let response = await chiama();
    if (response.status === 401 && (await refreshSession())) {
      response = await chiama();
    }
    if (!response.ok) return null;
    return (await response.json()) as UtenteCorrente;
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    return null;
  }
}

/** Solo per i test: azzera lo stato del modulo. */
export function resetAuthForTests(): void {
  refreshInFlight = null;
}
